/** Create or edit a lead: header, line items, commissions and a live preview. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Info, Plus, Save, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, percent } from '../../lib/format';
import { previewLead } from '../../lib/lead-preview';
import {
  Alert,
  Button,
  Card,
  Field,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface ParameterValue {
  id: string;
  code: string;
  label: string;
  meta: Record<string, unknown> | null;
}
interface ParameterGroup {
  key: string;
  values: ParameterValue[];
}
interface CustomerOption {
  id: string;
  name: string;
  code: string;
  isGovernment: boolean;
  contacts?: { id: string; name: string }[];
}
interface TaxSetting {
  vatRate: string;
  ewtGoods: string;
  ewtServices: string;
  wvatGov: string;
  ewtCommission: string;
  lowMarginThreshold: string;
}

interface ItemRow {
  name: string;
  description: string;
  unit: string;
  qty: string;
  amountVatEx: string;
}
interface CommissionRow {
  categoryId: string;
  payee: string;
  amount: string;
}

const BLANK_ITEM: ItemRow = { name: '', description: '', unit: '', qty: '1', amountVatEx: '' };

/** What the detail endpoint returns when editing an existing lead. */
interface ExistingLead {
  customerId: string;
  contactId: string | null;
  projectName: string;
  solutionCategoryId: string | null;
  stageId: string;
  proposalRef: string | null;
  bomId: string | null;
  bomDriveUrl: string | null;
  proposalDate: string | null;
  validityDays: number | null;
  expectedCloseDate: string | null;
  notes: string | null;
  amountVatEx: string;
  vatApplicable: boolean;
  taxClass: 'GOODS' | 'SERVICES' | 'MIXED';
  goodsAmount: string | null;
  servicesAmount: string | null;
  isGovernment: boolean;
  projectCost: string | null;
  items?: { name: string; description: string | null; unit: string | null; qty: string; amountVatEx: string }[];
  commissions?: { categoryId: string; payee: string; amount: string }[];
}

export function LeadFormPage(): JSX.Element {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [form, setForm] = useState({
    customerId: '',
    contactId: '',
    projectName: '',
    solutionCategoryId: '',
    stageId: '',
    proposalRef: '',
    bomId: '',
    bomDriveUrl: '',
    proposalDate: '',
    validityDays: '30',
    expectedCloseDate: '',
    notes: '',
    amountVatEx: '',
    /**
     * Proposals are written VAT-inclusive, so that is what a new lead expects
     * (boss, 26 Sep 2026). Editing keeps the basis the figures are stored in,
     * so a loaded lead never silently changes value.
     */
    amountsIncludeVat: true,
    vatApplicable: true,
    taxClass: 'SERVICES' as 'GOODS' | 'SERVICES' | 'MIXED',
    goodsAmount: '',
    servicesAmount: '',
    isGovernment: false,
    projectCost: '',
  });
  const [items, setItems] = useState<ItemRow[]>([{ ...BLANK_ITEM }]);
  const [commissions, setCommissions] = useState<CommissionRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
  });
  const tax = useQuery({
    queryKey: ['tax-settings'],
    queryFn: () => api<{ data: TaxSetting }>('/settings/tax'),
  });
  const customers = useQuery({
    queryKey: ['customers', 'options'],
    queryFn: () =>
      api<{ data: CustomerOption[] }>('/customers?pageSize=200&status=ACTIVE&sort=name'),
  });
  const contacts = useQuery({
    queryKey: ['customer', form.customerId],
    queryFn: () =>
      api<{ data: { contacts: { id: string; name: string; designation: string | null }[] } }>(
        `/customers/${form.customerId}`,
      ),
    enabled: Boolean(form.customerId),
  });
  const existing = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api<{ data: ExistingLead }>(`/leads/${id}`),
    enabled: editing,
  });

  useEffect(() => {
    const row = existing.data?.data;
    if (!row) return;
    setForm({
      customerId: row.customerId,
      contactId: row.contactId ?? '',
      projectName: row.projectName,
      solutionCategoryId: row.solutionCategoryId ?? '',
      stageId: row.stageId,
      proposalRef: row.proposalRef ?? '',
      bomId: row.bomId ?? '',
      bomDriveUrl: row.bomDriveUrl ?? '',
      proposalDate: row.proposalDate ? String(row.proposalDate).slice(0, 10) : '',
      validityDays: row.validityDays ? String(row.validityDays) : '',
      expectedCloseDate: row.expectedCloseDate ? String(row.expectedCloseDate).slice(0, 10) : '',
      notes: row.notes ?? '',
      amountVatEx: String(row.amountVatEx),
      amountsIncludeVat: false,
      vatApplicable: row.vatApplicable,
      taxClass: row.taxClass,
      goodsAmount: row.goodsAmount ? String(row.goodsAmount) : '',
      servicesAmount: row.servicesAmount ? String(row.servicesAmount) : '',
      isGovernment: row.isGovernment,
      projectCost: row.projectCost ? String(row.projectCost) : '',
    });
    setItems(
      (row.items ?? []).map((item) => ({
        name: item.name,
        description: item.description ?? '',
        unit: item.unit ?? '',
        qty: String(item.qty ?? 1),
        amountVatEx: String(item.amountVatEx),
      })),
    );
    setCommissions(
      (row.commissions ?? []).map((commission) => ({
        categoryId: commission.categoryId,
        payee: commission.payee,
        amount: String(commission.amount),
      })),
    );
  }, [existing.data]);

  const stages = parameters.data?.data.find((g) => g.key === 'lead_stage')?.values ?? [];
  const solutions = parameters.data?.data.find((g) => g.key === 'solution_category')?.values ?? [];

  // Default to the first open stage when creating.
  useEffect(() => {
    if (!editing && !form.stageId && stages.length > 0) {
      const prospecting = stages.find((stage) => stage.code === 'PROSPECTING') ?? stages[0];
      setForm((current) => ({ ...current, stageId: prospecting.id }));
    }
  }, [editing, form.stageId, stages]);

  const rates = tax.data?.data;
  const stageProbability = useMemo(() => {
    const stage = stages.find((value) => value.id === form.stageId);
    return Number((stage?.meta as { probability?: number } | null)?.probability ?? 0);
  }, [stages, form.stageId]);

  /** Strips VAT when the figures were typed as they appear on the proposal. */
  const vatRate = Number(rates?.vatRate ?? 0.12);
  const toEx = useCallback(
    (value: string | number): number => {
      const amount = Number(value || 0);
      if (!form.amountsIncludeVat || !form.vatApplicable || vatRate <= 0) return amount;
      return Math.round((amount / (1 + vatRate)) * 100) / 100;
    },
    [form.amountsIncludeVat, form.vatApplicable, vatRate],
  );

  const preview = useMemo(
    () =>
      previewLead({
        amountVatEx: String(toEx(form.amountVatEx)),
        vatApplicable: form.vatApplicable,
        vatRate: Number(rates?.vatRate ?? 0.12),
        taxClass: form.taxClass,
        goodsAmount: String(toEx(form.goodsAmount)),
        servicesAmount: String(toEx(form.servicesAmount)),
        ewtGoods: Number(rates?.ewtGoods ?? 0.01),
        ewtServices: Number(rates?.ewtServices ?? 0.02),
        isGovernment: form.isGovernment,
        wvatGov: Number(rates?.wvatGov ?? 0.05),
        projectCost: String(toEx(form.projectCost)),
        commissions,
        ewtCommission: Number(rates?.ewtCommission ?? 0.1),
        stageProbability,
      }),
    [form, commissions, rates, stageProbability, toEx],
  );

  const itemsTotal = items
    .filter((item) => item.name.trim())
    .reduce((total, item) => total + toEx(item.amountVatEx), 0);
  const hasItems = items.some((item) => item.name.trim() !== '');
  // Lines are optional now; they only have to add up if someone listed them.
  const itemsMatch = !hasItems || Math.abs(itemsTotal - preview.amountVatEx) < 0.005;
  const lowMargin = preview.netMarginPct < Number(rates?.lowMarginThreshold ?? 0.1);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        customerId: form.customerId,
        contactId: form.contactId || undefined,
        projectName: form.projectName.trim(),
        solutionCategoryId: form.solutionCategoryId || undefined,
        stageId: form.stageId || undefined,
        proposalRef: form.proposalRef || undefined,
        bomId: form.bomId || undefined,
        bomDriveUrl: form.bomDriveUrl || undefined,
        proposalDate: form.proposalDate || undefined,
        validityDays: form.validityDays ? Number(form.validityDays) : undefined,
        expectedCloseDate: form.expectedCloseDate || undefined,
        notes: form.notes || undefined,
        amountVatEx: Number(form.amountVatEx || 0),
        amountsIncludeVat: form.amountsIncludeVat,
        vatApplicable: form.vatApplicable,
        taxClass: form.taxClass,
        goodsAmount: form.taxClass === 'MIXED' ? Number(form.goodsAmount || 0) : undefined,
        servicesAmount: form.taxClass === 'MIXED' ? Number(form.servicesAmount || 0) : undefined,
        isGovernment: form.isGovernment,
        projectCost: Number(form.projectCost || 0),
        items: items
          .filter((item) => item.name.trim())
          .map((item) => ({
            name: item.name.trim(),
            description: item.description || undefined,
            unit: item.unit || undefined,
            qty: Number(item.qty || 1),
            amountVatEx: Number(item.amountVatEx || 0),
          })),
      };
      return editing
        ? api<{ data: { id: string } }>(`/leads/${id}`, { method: 'PATCH', body })
        : api<{ data: { id: string } }>('/leads', { method: 'POST', body });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate(`/leads/${result.data.id}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the lead.'),
  });

  if (editing && existing.isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">
          {editing ? 'Edit lead' : 'New lead'}
        </h1>
        <p className="text-sm text-slate-500">
          A summary of the proposal — deliverables and money. The detailed bill of materials stays
          in Google Drive.
        </p>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}

      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <Card title="The deal">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Customer" required>
              <select
                className={inputClass}
                required
                value={form.customerId}
                onChange={(event) => {
                  const customer = customers.data?.data.find((c) => c.id === event.target.value);
                  setForm({
                    ...form,
                    customerId: event.target.value,
                    contactId: '',
                    isGovernment: customer?.isGovernment ?? form.isGovernment,
                  });
                }}
              >
                <option value="">Choose a customer</option>
                {customers.data?.data.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Contact person">
              <select
                className={inputClass}
                value={form.contactId}
                onChange={(event) => setForm({ ...form, contactId: event.target.value })}
                disabled={!form.customerId}
              >
                <option value="">Not set</option>
                {contacts.data?.data.contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}
                    {contact.designation ? ` — ${contact.designation}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Project name" required>
                <input
                  className={inputClass}
                  required
                  value={form.projectName}
                  onChange={(event) => setForm({ ...form, projectName: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Solution category">
              <select
                className={inputClass}
                value={form.solutionCategoryId}
                onChange={(event) => setForm({ ...form, solutionCategoryId: event.target.value })}
              >
                <option value="">Not set</option>
                {solutions.map((solution) => (
                  <option key={solution.id} value={solution.id}>
                    {solution.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Stage" required>
              <select
                className={inputClass}
                required
                value={form.stageId}
                onChange={(event) => setForm({ ...form, stageId: event.target.value })}
              >
                {stages
                  .filter((stage) => stage.code !== 'CLOSED_WON')
                  .map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Proposal reference" hint="e.g. PRP-202609-ARMD-001-V1">
              <input
                className={inputClass}
                value={form.proposalRef}
                onChange={(event) => setForm({ ...form, proposalRef: event.target.value })}
              />
            </Field>
            <Field label="BOM ID" hint="e.g. BOM-202609-ARMD-001-V1">
              <input
                className={inputClass}
                value={form.bomId}
                onChange={(event) => setForm({ ...form, bomId: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="BOM Google Drive link" hint="Opens in a new tab.">
                <input
                  className={inputClass}
                  type="url"
                  placeholder="https://drive.google.com/…"
                  value={form.bomDriveUrl}
                  onChange={(event) => setForm({ ...form, bomDriveUrl: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Proposal date">
              <input
                className={inputClass}
                type="date"
                value={form.proposalDate}
                onChange={(event) => setForm({ ...form, proposalDate: event.target.value })}
              />
            </Field>
            <Field label="Validity (days)">
              <input
                className={inputClass}
                type="number"
                min={0}
                value={form.validityDays}
                onChange={(event) => setForm({ ...form, validityDays: event.target.value })}
              />
            </Field>
            <Field label="Expected close date">
              <input
                className={inputClass}
                type="date"
                value={form.expectedCloseDate}
                onChange={(event) => setForm({ ...form, expectedCloseDate: event.target.value })}
              />
            </Field>
          </div>
        </Card>

        <Card
          title="Line items (optional)"
          description="Deliverables are decided on the project board after confirmation, and the proposal and BOM carry the detail. List lines here only if you want them on the sales order — and then they must add up to the total."
          actions={
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setItems([...items, { ...BLANK_ITEM }])}
            >
              <Plus aria-hidden="true" className="h-4 w-4" /> Add line
            </Button>
          }
        >
          <TableWrap>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Deliverable</Th>
                <Th>Unit</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Amount (VAT-ex)</Th>
                <Th align="right"> </Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((item, index) => (
                <tr key={index}>
                  <Td>{index + 1}</Td>
                  <Td>
                    <input
                      className={inputClass}
                      placeholder="Supply & install 12 UHF RFID readers"
                      value={item.name}
                      onChange={(event) => {
                        const next = [...items];
                        next[index] = { ...item, name: event.target.value };
                        setItems(next);
                      }}
                    />
                    <input
                      className={`${inputClass} mt-1 text-xs`}
                      placeholder="Description (optional)"
                      value={item.description}
                      onChange={(event) => {
                        const next = [...items];
                        next[index] = { ...item, description: event.target.value };
                        setItems(next);
                      }}
                    />
                  </Td>
                  <Td>
                    <input
                      className={`${inputClass} w-20`}
                      placeholder="lot"
                      value={item.unit}
                      onChange={(event) => {
                        const next = [...items];
                        next[index] = { ...item, unit: event.target.value };
                        setItems(next);
                      }}
                    />
                  </Td>
                  <Td align="right">
                    <input
                      className={`${inputClass} w-20 text-right`}
                      type="number"
                      step="0.01"
                      value={item.qty}
                      onChange={(event) => {
                        const next = [...items];
                        next[index] = { ...item, qty: event.target.value };
                        setItems(next);
                      }}
                    />
                  </Td>
                  <Td align="right">
                    <input
                      className={`${inputClass} w-36 text-right tabular`}
                      type="number"
                      step="0.01"
                      value={item.amountVatEx}
                      onChange={(event) => {
                        const next = [...items];
                        next[index] = { ...item, amountVatEx: event.target.value };
                        setItems(next);
                      }}
                    />
                  </Td>
                  <Td align="right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setItems(items.filter((_, i) => i !== index))}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4 text-red-600" />
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 font-semibold dark:border-slate-700">
                <Td>{''}</Td>
                <Td>Line items total</Td>
                <Td>{''}</Td>
                <Td>{''}</Td>
                <Td align="right" className="tabular">
                  {peso(itemsTotal)}
                </Td>
                <Td>{''}</Td>
              </tr>
            </tfoot>
          </TableWrap>

          {!itemsMatch && Number(form.amountVatEx) > 0 && (
            <div className="mt-3">
              <Alert tone="warning">
                The lines add up to {peso(itemsTotal)}, but the total project amount is{' '}
                {peso(preview.amountVatEx)}. Either make them match, or clear them — lines are
                optional.
              </Alert>
            </div>
          )}
        </Card>

        <Card title="Money">
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              The amounts below are
            </span>
            <select
              className={`${inputClass} w-56`}
              value={form.amountsIncludeVat ? 'INC' : 'EX'}
              onChange={(event) =>
                setForm({ ...form, amountsIncludeVat: event.target.value === 'INC' })
              }
              aria-label="Whether the amounts entered include VAT"
            >
              <option value="INC">VAT inclusive, as on the proposal</option>
              <option value="EX">VAT exclusive</option>
            </select>
            <span className="text-xs text-slate-500">
              {form.amountsIncludeVat
                ? 'VAT is worked out and taken off for you; everything below is stored VAT-exclusive.'
                : 'Figures are used as typed.'}
            </span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={
                form.amountsIncludeVat
                  ? 'Total project amount, VAT inclusive'
                  : 'Total project amount, VAT-exclusive'
              }
              hint={
                form.amountsIncludeVat && form.vatApplicable
                  ? `= ${peso(preview.amountVatEx)} before VAT`
                  : 'The contract value before VAT.'
              }
              required
            >
              <input
                className={`${inputClass} tabular`}
                type="number"
                step="0.01"
                min={0}
                required
                value={form.amountVatEx}
                onChange={(event) => setForm({ ...form, amountVatEx: event.target.value })}
              />
            </Field>
            <Field
              label={
                form.amountsIncludeVat
                  ? 'Total project cost, VAT inclusive'
                  : 'Total project cost (from the BOM, VAT-ex)'
              }
              hint={
                form.amountsIncludeVat && form.vatApplicable && form.projectCost
                  ? `= ${peso(toEx(form.projectCost))} before VAT`
                  : undefined
              }
            >
              <input
                className={`${inputClass} tabular`}
                type="number"
                step="0.01"
                min={0}
                value={form.projectCost}
                onChange={(event) => setForm({ ...form, projectCost: event.target.value })}
              />
            </Field>
            <Field label="Tax class for withholding">
              <select
                className={inputClass}
                value={form.taxClass}
                onChange={(event) =>
                  setForm({ ...form, taxClass: event.target.value as typeof form.taxClass })
                }
              >
                <option value="SERVICES">Services (2%)</option>
                <option value="GOODS">Goods (1%)</option>
                <option value="MIXED">Mixed</option>
              </select>
            </Field>
            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                  checked={form.vatApplicable}
                  onChange={(event) => setForm({ ...form, vatApplicable: event.target.checked })}
                />
                VAT applies ({percent(Number(rates?.vatRate ?? 0.12))})
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                  checked={form.isGovernment}
                  onChange={(event) => setForm({ ...form, isGovernment: event.target.checked })}
                />
                Government client (5% withholding VAT)
              </label>
            </div>
            {form.taxClass === 'MIXED' && (
              <>
                <Field label="Goods portion (VAT-ex)" required>
                  <input
                    className={`${inputClass} tabular`}
                    type="number"
                    step="0.01"
                    required
                    value={form.goodsAmount}
                    onChange={(event) => setForm({ ...form, goodsAmount: event.target.value })}
                  />
                </Field>
                <Field label="Services portion (VAT-ex)" required>
                  <input
                    className={`${inputClass} tabular`}
                    type="number"
                    step="0.01"
                    required
                    value={form.servicesAmount}
                    onChange={(event) => setForm({ ...form, servicesAmount: event.target.value })}
                  />
                </Field>
              </>
            )}
          </div>
        </Card>

        {can('leads.view_commission') && (
          <Card
            title="Commissions"
            description="Added, edited and deleted on the lead page (Commissions card), one payee at a time, or pushed from the BOM Section D through the API."
          >
            <p className="text-sm text-slate-500">
              {commissions.length === 0
                ? 'No commissions on this deal yet.'
                : `${commissions.length} on this deal: ${peso(preview.totalCommission)} gross, ${peso(preview.commissionEwt)} withheld; included in the net profit below.`}
            </p>
          </Card>
        )}

        <Card title="Figures" description="Calculated by the server when you save.">
          <div className="grid gap-4 sm:grid-cols-3">
            <Figure label="VAT" value={peso(preview.vat)} />
            <Figure label="Total contract price" value={peso(preview.totalContractPrice)} strong />
            <Figure label="Expected withholding (EWT)" value={peso(preview.ewtAmount)} />
            {form.isGovernment && (
              <Figure label="Withholding VAT (government)" value={peso(preview.wvatAmount)} />
            )}
            <Figure
              label="Expected net collection"
              value={peso(preview.expectedNetCollection)}
              strong
            />
            {can('leads.view_financials') && (
              <>
                <Figure
                  label="Gross profit"
                  value={peso(preview.grossProfit)}
                  sub={percent(preview.grossMarginPct)}
                />
                <Figure
                  label="Net profit"
                  value={peso(preview.netProfit)}
                  sub={percent(preview.netMarginPct)}
                  danger={preview.netProfit < 0}
                />
                <Figure label="Weighted amount" value={peso(preview.weightedAmount)} />
              </>
            )}
          </div>

          {can('leads.view_financials') && lowMargin && Number(form.amountVatEx) > 0 && (
            <div className="mt-4">
              <Alert tone={preview.netProfit < 0 ? 'critical' : 'warning'}>
                <span className="flex gap-2">
                  <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                  {preview.netProfit < 0
                    ? 'This deal loses money as priced.'
                    : `Net margin is ${percent(preview.netMarginPct)}, below the ${percent(Number(rates?.lowMarginThreshold ?? 0.1))} threshold.`}
                </span>
              </Alert>
            </div>
          )}

          <div className="mt-4 space-y-2 text-xs text-slate-500">
            <p className="flex gap-2">
              <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Profit is measured after removing VAT, because VAT is collected for BIR and passed on.
              Corporate income tax is not deducted per project.
            </p>
            <p className="flex gap-2">
              <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Withholding tax and withholding VAT are creditable against your own tax due. They
              reduce the cash collected, not the profit.
            </p>
          </div>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" busy={save.isPending}>
            <Save aria-hidden="true" className="h-4 w-4" />
            {editing ? 'Save changes' : 'Create lead'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          {!itemsMatch && Number(form.amountVatEx) > 0 && (
            <StatusChip tone="warning">Line items do not match the total yet</StatusChip>
          )}
        </div>
      </form>
    </div>
  );
}

function Figure({
  label,
  value,
  sub,
  strong,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  strong?: boolean;
  danger?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-800">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={`tabular ${strong ? 'text-lg font-bold' : 'text-base font-semibold'} ${
          danger ? 'text-red-600' : 'text-navy dark:text-white'
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}
